'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { showToast } from '@cineamo/ui/src/helpers/toast-helper';

import { ButtonStyle } from '@cineamo/ui/src/button/ButtonStyle.enum';
import Button from '@cineamo/ui/src/button/NewButton';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { SubscriberStatus, useNewsletterModal } from '@/providers/NewsletterModalProvider';

import GroupedTopicToggles, { topicKey } from './GroupedTopicToggles';
import PreferenceToggle from './PreferenceToggle';
import { NewsletterTopic, NewsletterTopicGroup, useNewsletterTopics } from './useNewsletterTopics';

type Props = {
    subscriberStatus: SubscriberStatus;
};

export default function NewsletterPreferencesView({ subscriberStatus }: Props) {
    const t = useTranslations();
    const domainConfiguration = useDomainConfiguration();
    const { refreshSubscriberStatus } = useNewsletterModal();
    const { allTopics } = useNewsletterTopics();

    const subscribedKeys = useMemo(() => {
        const set = new Set<string>();
        (subscriberStatus.topics ?? []).forEach((tt) => set.add(topicKey(tt.type, tt.referenceId)));
        return set;
    }, [subscriberStatus.topics]);

    const [desiredKeys, setDesiredKeys] = useState<Set<string>>(() => new Set(subscribedKeys));
    const [unsubscribeAll, setUnsubscribeAll] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    React.useEffect(() => {
        setDesiredKeys(new Set(subscribedKeys));
        setUnsubscribeAll(false);
    }, [subscribedKeys]);

    const isDirty = useMemo(() => {
        if (unsubscribeAll) return true;
        if (desiredKeys.size !== subscribedKeys.size) return true;
        for (const k of desiredKeys) {
            if (!subscribedKeys.has(k)) return true;
        }
        return false;
    }, [desiredKeys, subscribedKeys, unsubscribeAll]);

    const toggleTopic = (topic: NewsletterTopic) => {
        const k = topicKey(topic.type, topic.referenceId);
        setDesiredKeys((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
        });
    };

    const setGroup = (group: NewsletterTopicGroup, allOn: boolean) => {
        setDesiredKeys((prev) => {
            const next = new Set(prev);
            allTopics
                .filter((tp) => tp.group === group)
                .forEach((tp) => {
                    const k = topicKey(tp.type, tp.referenceId);
                    if (allOn) next.add(k);
                    else next.delete(k);
                });
            return next;
        });
    };

    const handleSave = async () => {
        const cinemaId = domainConfiguration.cinema?.id;
        if (!cinemaId) return;
        setIsSaving(true);

        try {
            if (unsubscribeAll) {
                const token = subscriberStatus.unsubscribeToken;
                if (!token) throw new Error('Missing unsubscribe token');
                const res = await fetch('/api/newsletter/unsubscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token })
                });
                if (!res.ok) throw new Error('Unsubscribe failed');
                showToast({
                    toastType: toast.success,
                    messageBody: t('newsletter-preferences.unsubscribe-success')
                });
                refreshSubscriberStatus();
                return;
            }

            const desiredTopics: { type: string; referenceId: string | null }[] = [];
            allTopics.forEach((topic) => {
                const k = topicKey(topic.type, topic.referenceId);
                if (desiredKeys.has(k)) {
                    desiredTopics.push({
                        type: topic.type,
                        referenceId: topic.referenceId ?? null
                    });
                }
            });

            const res = await fetch('/api/newsletter/preferences', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cinemaId, topics: desiredTopics })
            });
            if (!res.ok) throw new Error('Save failed');
            showToast({
                toastType: toast.success,
                messageBody: t('newsletter-preferences.save-success')
            });
            refreshSubscriberStatus();
        } catch {
            showToast({
                toastType: toast.error,
                messageTitle: t('newsletter-modal.error.title'),
                messageBody: unsubscribeAll
                    ? t('newsletter-preferences.unsubscribe-error')
                    : t('newsletter-preferences.save-error')
            });
        } finally {
            setIsSaving(false);
        }
    };

    const saveDisabled = !isDirty || isSaving;

    return (
        <div className="flex flex-col gap-24 py-32 max-w-[820px] mx-auto px-16 text-[var(--on-background-color)]">
            <div className="flex flex-col gap-12">
                <h1 className="text-headline-24">{t('newsletter-preferences.title')}</h1>
                <p className="text-body-bold-14">{t('newsletter-preferences.intro')}</p>
            </div>

            <GroupedTopicToggles
                desiredKeys={desiredKeys}
                onToggleTopic={toggleTopic}
                onSetGroup={setGroup}
                disabled={unsubscribeAll}
            />

            <section className="flex flex-col gap-16 mt-24">
                <h2 className="text-body-bold-14 text-[var(--on-background-color)]">
                    {t('newsletter-preferences.unsubscribe.section-title')}
                </h2>
                <PreferenceToggle
                    label={t('newsletter-preferences.unsubscribe.toggle-label')}
                    checked={unsubscribeAll}
                    onChange={setUnsubscribeAll}
                />
            </section>

            <Button
                className="self-start"
                style={ButtonStyle.default}
                onClick={handleSave}
                disabled={saveDisabled}>
                {isSaving ? t('newsletter-preferences.saving') : t('newsletter-preferences.save')}
            </Button>
        </div>
    );
}
